import React, { useState, useEffect } from 'react';
import { Sidebar } from './Sidebar';
import { PanelLeft, GraduationCap, Sun, Moon, Coffee } from 'lucide-react';
import { useData } from '../context/DataContext';
import { translations } from '../lib/i18n';
import { getSavedSidebarOpen, saveSidebarOpen } from '../lib/storage';
import type { Language } from '../lib/types';
import { Link } from 'react-router-dom';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const { language, setLanguage, theme, setTheme } = useData();
  const t = translations[language];

  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth < 960 : false
  );

  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 960) {
      return false;
    }
    return getSavedSidebarOpen();
  });

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 960;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleToggleSidebar = () => {
    setSidebarOpen((prev) => {
      const next = !prev;
      if (!isMobile) {
        saveSidebarOpen(next);
      }
      return next;
    });
  };

  return (
    <div className={`dashboard-shell ${sidebarOpen ? 'sidebar-expanded' : 'sidebar-mini'}`}>
      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onToggle={handleToggleSidebar}
        isMobile={isMobile}
        onCloseMobile={() => setSidebarOpen(false)}
      />

      {/* Main Content Area */}
      <div className="dashboard-main-area">
        {/* Top Navbar */}
        <header className="dashboard-top-navbar">
          <div className="top-navbar-start">
            <button
              type="button"
              className="top-navbar-toggle-btn"
              onClick={handleToggleSidebar}
              title={sidebarOpen ? t.collapseSidebar : t.expandSidebar}
              aria-label="Toggle menu"
            >
              <PanelLeft size={19} />
            </button>

            <Link to="/" className="top-navbar-brand-mobile">
              <GraduationCap size={16} />
              <span>{t.appName}</span>
            </Link>
          </div>

          <div className="top-navbar-end">
            {/* Quick Lang/Theme pill in top bar */}
            <div className="controls-glass-pill" role="toolbar" aria-label="Controls">
              {(['ar', 'fr', 'en'] as Language[]).map((lng) => (
                <button
                  key={lng}
                  type="button"
                  className={`control-btn ${language === lng ? 'active' : ''}`}
                  onClick={() => setLanguage(lng)}
                >
                  {lng.toUpperCase()}
                </button>
              ))}

              <span className="glass-pill-divider" aria-hidden="true" />

              <button
                type="button"
                className={`theme-icon-btn ${theme === 'light' ? 'active' : ''}`}
                onClick={() => setTheme('light')}
                title={t.themeLight}
              >
                <Sun size={14} />
              </button>
              <button
                type="button"
                className={`theme-icon-btn ${theme === 'cream' ? 'active' : ''}`}
                onClick={() => setTheme('cream')}
                title={t.themeCream}
              >
                <Coffee size={14} />
              </button>
              <button
                type="button"
                className={`theme-icon-btn ${theme === 'dark' ? 'active' : ''}`}
                onClick={() => setTheme('dark')}
                title={t.themeDark}
              >
                <Moon size={14} />
              </button>
            </div>
          </div>
        </header>

        {/* Viewport Content */}
        <div className="dashboard-content-scroll">{children}</div>
      </div>
    </div>
  );
};
